import { Box, Button, CircularProgress, ClickAwayListener, Dialog, Fab, FormControl, FormHelperText, IconButton, ListSubheader, MenuItem, MenuList, Popover, Popper, Select, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { observer } from 'mobx-react'
import { v4 as uuidv4 } from 'uuid'
import { driver } from 'driver.js'
import * as Yup from 'yup'
import dayjs from 'dayjs'

import buttonWaveSimba from '../../../assets/simba-ai/buttonwavesimba.png'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import { formatDate, Icons } from '../../../utils/utilities'
import { useUserStore } from '../../../store/user.store'
import api from '../../../service/api'
import CustomChip from '../../common/CustomChip'
import CustomSelectField from '../../common/CustomSelectField'
import { toast } from 'react-toastify'

const accessGroupsFilterFunc = ( item, values, filterEntity ) => {

    if ( filterEntity === "batch" ) {
        if ( item.entity !== 'batch' ) return false

        if ( !item.batch_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( values.sem && item.current_sem && values.sem !== item.current_sem ) return false

        return true
    } else {

        if ( item.entity !== 'section' ) return false

        if ( !item.section_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( item.current_sem && values.sem && values.sem !== item.current_sem ) return false

        return true
    }
}

const complexityOptions = [
    { value: "EASY", label: "Easy" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HARD", label: "Hard" },
]

const MODULE_OPTIONS = Array.from( { length: 5 }, ( _, i ) => `Module ${i + 1}` )

const AddModuleButton = ( { label = "Add Module", onselect, modules } ) => {

    const [anchorEl, setAnchorEl] = useState( null )
    const [selected, setSelected] = useState( '' )

    const { zIndex, palette, layout, shadows, border } = useTheme()


    const handleClickOnModule = ( module ) => {
        onselect( module )
        setAnchorEl( null )
    }

    return (
        <Box>
            <Select id="moduleAdder" sx={{ color: !selected && palette.form.placeholder }} displayEmpty size="small" value={selected} fullWidth onChange={event => setAnchorEl( event.target.value )} color='customThemeColor' variant='outlined'>
                <MenuItem disabled value="" dense>Select Module</MenuItem>
                {modules.map( module => (
                    <MenuItem id={`moduleOption${module.replace( /\s/g, "" )}`} dense key={module} value={module} onClick={() => handleClickOnModule( module )} >{module}</MenuItem>
                ) )}
            </Select>
        </Box>
    )
}


const validationSchema = Yup.object().shape( {
    title: Yup.string().required( "Title field is required" ).max( 200, "Maximum characters: 200" ),
    modules_and_topics: Yup
        .object()
        .required( "Practice session must have atleast one module" )
        .test( "ATLEAST_ONE_MODULE", "Practice session must have atleast one module", ( value ) => {
            return Object.keys( value ).length > 0
        } )
        .test( "ATLEAST_ONE_TOPIC_IN_EACH_MODULE", "All modules should have atleast one topic.", ( value ) => {
            return Object.keys( value ).every( mod => value[mod].topics.length > 0 )
        } )
        .test( "ALL_MODULE_HAVE_NAME", "All module should have valid names.", ( value ) => {
            return Object.keys( value ).every( mod => Boolean( value[mod].name?.trim() ) )
        } ),
    access_groups: Yup
        .mixed( "Access groups must be a array." )
        .required( "Atleast one batch or section must be specified." )
        .test( "ATLEAST_ONE_ACCESS_GROUPS", "Practice session must have atleast one bacth or section", ( value ) => {
            return value.length > 0
        } ),
    subject: Yup.mixed().required( "Subject details are required." ),
    date_range_start: Yup
        .mixed()
        .required( "Date range values are required." )
        .test( "START_LESS_THAN_END", "Date range start should be less than end date.", ( value, { parent: { date_range_end } } ) => {
            return value < date_range_end
        } )
        .test( "START_LESS_DIFF_BETWEEN_15_DAYS", "Practice session date range should not exceed 15 days.", ( value, { parent: { date_range_end } } ) => {
            console.log( date_range_end.diff( value, 'day' ) )

            return date_range_end.diff( value, 'day' ) <= 15
        } )
} )

const EditPracticeSession = observer( ( { participants = [], practiceSession = null, mode = "create", open, onClose } ) => {

    const [uploadingAttachments, setUploadingAttachments] = useState( [] )
    const [fetchingSubjects, setFetchingSubjects] = useState( false )
    const [refreshing, setRefreshing] = useState( false )
    const [creating, setCreating] = useState( false )

    const { border, palette, layout, components, breakpoints } = useTheme()

    const SimbaAIStore = useSimbaAIStore()
    const UserStore = useUserStore()

    const sm = useMediaQuery( breakpoints.up( 'sm' ) )

    const initialValues = useMemo( () => (
        {
            title: practiceSession.title,
            modules_and_topics: practiceSession.modules_and_topics,
            module: "",
            topics: practiceSession.topics,
            topic: "",
            date_range_start: dayjs( practiceSession.date_range_start ),
            date_range_end: dayjs( practiceSession.date_range_end ),
            academicYear: practiceSession.subject.ac_year_id,
            subject: "",
            access_groups: practiceSession.access_groups,
            selected_access_groups: practiceSession.access_groups?.map( ag => ag.id ),
            reference_materials: practiceSession.reference_materials?.map( rm => rm._id ),
            complexity: practiceSession.complexity || "EASY",
            subject: {
                subject_id: practiceSession?.subject?.subject_id,
                subject_code: practiceSession?.subject?.subject_code,
                subject_name_short: practiceSession?.subject?.subject_name_short,
                subject_name: practiceSession?.subject?.subject_name,
                ac_year_id: practiceSession?.subject?.ac_year_id,
                ac_year: practiceSession?.subject?.ac_year,
                year: practiceSession?.subject?.year,
                sem: practiceSession?.subject?.sem,
            },
            year: practiceSession.subject.year,
            sem: practiceSession.subject.sem,
            close_date: practiceSession.close_date ? dayjs( practiceSession.close_date ).add( 1, 'day' ) : null
        }
    ), [practiceSession] )

    const updateModuleName = ( moduleKey, name, values, setFieldValue ) => {
        setFieldValue( 'modules_and_topics', {
            ...values.modules_and_topics,
            [moduleKey]: {
                ...values.modules_and_topics[moduleKey],
                name
            }
        } )
    }

    const handleAcademicYearChange = ( event, setFieldValue ) => {
        setFieldValue( 'academicYear', event.target.value )
        setFieldValue( 'subject', null )
        setFieldValue( 'year', '' )
        setFieldValue( 'sem', '' )
    }

    const addTopic = ( moduleKey, topic, values, setFieldValue ) => {
        if ( !topic ) return toast( "Topic name is required to add topic." )

        const topics = new Set( values.modules_and_topics[moduleKey].topics )
        topics.add( topic )

        setFieldValue( 'modules_and_topics', {
            ...values.modules_and_topics,
            [moduleKey]: {
                ...values.modules_and_topics[moduleKey],
                topics: Array.from( topics )
            }
        } )
    }

    const removeTopic = ( moduleKey, topicIndex, values, setFieldValue ) => {
        const topics = [...values.modules_and_topics[moduleKey].topics]
        topics.splice( topicIndex, 1 )

        setFieldValue( 'modules_and_topics', {
            ...values.modules_and_topics,
            [moduleKey]: {
                ...values.modules_and_topics[moduleKey],
                topics
            }
        } )
    }

    const removeModule = ( moduleKey, values, setFieldValue ) => {
        const updated = { ...values.modules_and_topics }
        delete updated[moduleKey]
        setFieldValue( 'modules_and_topics', updated )
    }

    const getBatches = async ( year, refresh = false ) => {
        setRefreshing( "batches" )
        await UserStore.fetchUserBatchesAndSection( year, refresh )
        setRefreshing( null )
    }

    const refreshYears = async () => {
        setRefreshing( "years" )
        await UserStore.fetchUserAcademicYears( true )
        setRefreshing( null )
    }

    const refreshBatches = async ( academicYear ) => {
        if ( academicYear )
            await getBatches( academicYear, true )
    }

    const removeSelectedAccessGroup = ( event, index, values, setFieldValue ) => {
        event.stopPropagation()
        const newSelectedIds = values.selected_access_groups || []
        const newSelectedGroups = values.access_groups || []
        newSelectedIds.splice( index, 1 )
        newSelectedGroups.splice( index, 1 )
        setFieldValue( 'access_groups', newSelectedIds )
        setFieldValue( 'access_groups', newSelectedGroups )
    }

    const handleAccessGroupChange = ( event, setFieldValue ) => {
        const value = event.target.value
        console.log( value )
        setFieldValue( 'selected_access_groups', value )
        const accessGroups = value.map( sag => UserStore.getUsersAllBatchesAndSectionList.find( ag => ag.id === sag ) )
        setFieldValue( 'access_groups', accessGroups )
    }

    const handleNewModuleSelect = ( module, values, setFieldValue ) => {
        if ( !values.modules_and_topics[module] ) {
            setFieldValue( 'modules_and_topics', {
                ...values.modules_and_topics,
                [module]: { name: "" || "", topics: [] }
            } )
        }
    }

    const handleSubjectChange = async ( e, setValue, date ) => {
        const subjectDetails = UserStore.getUserSubjectsDetails?.currentSubjects.find( s => s?.subject_id === e.target.value )
        const subjectValue = {
            subject_id: subjectDetails.subject_id,
            subject_code: subjectDetails.subject_code,
            subject_name_short: subjectDetails.subject_name_short,
            subject_name: subjectDetails.subject_name,
            ac_year_id: subjectDetails.ac_year_id,
            ac_year: subjectDetails.ac_year,
        }
        setValue( 'subject', subjectValue )
    }

    const handleNewTopicAdd = ( event, module, values, setFieldValue ) => {
        if ( event.type === 'keydown' && event.key === "Enter" ) {
            event.preventDefault()
            addTopic(
                module,
                event.target.value.trim(),
                values,
                setFieldValue
            )
            event.target.value = ""
            setFieldValue( `${module}-topicInput`, "" )
        } else if ( event.type === 'click' ) {
            addTopic(
                module,
                values[`${module}-topicInput`].trim(),
                values,
                setFieldValue
            )
            event.target.value = ""
            setFieldValue( `${module}-topicInput`, "" )
            event.target.focus()
        }
    }

    const createPracticeSessions = async ( values, { resetForm } ) => {
        setCreating( true )
        const { success, data } = await SimbaAIStore.createPracticeSession( values )

        if ( success ) {
            resetForm()
            onClose()
        }
        setCreating( false )
    }
    const updatePracticeSession = async ( values, { resetForm } ) => {
        setCreating( true )
        await api.simbaAI.updatePracticeSession( practiceSession._id, values )
        onClose()
        setCreating( false )
        resetForm()
    }

    const getFacultySubjects = useCallback( async () => {
        setFetchingSubjects( true )
        if ( UserStore.getErrorsStatuses?.subjects )
            await UserStore.fetchUserSubjects()
        setFetchingSubjects( false )
    }, [UserStore] )

    // const getReferenceMaterials = useCallback( async () => {
    //     setLoadingReferenceMaterials( true )
    //     if ( SimbaAIStore.getErrors.referenceMaterials )
    //         await SimbaAIStore.fetchReferenceMaterials()
    //     setLoadingReferenceMaterials( false )
    // }, [SimbaAIStore] )

    useEffect( () => {
        getFacultySubjects()
    }, [getFacultySubjects] )

    return (
        <Dialog
            open={open}

        >
            <Box display="flex" flexDirection="column" position="relative" overflow="auto">

                {/* <Box accept='.pdf,application/pdf' onChange={onFileSelect} ref={refUploadInputRef} overflow="hidden" position="absolute" maxHeight="0" maxWidth="0" component="input" type="file" multiple /> */}

                {/* <ReferenceMaterialUploadDialog attachments={uploadingAttachments} setAttachments={setUploadingAttachments} open={uploadingAttachments.length > 0} onClose={() => setUploadingAttachments( [] )} /> */}
                <Box position="sticky" borderBottom={border[1]} top="0" bgcolor={palette.form.formCardBg} sx={{ zIndex: 5 }} display="flex" justifyContent="space-between" gap="20px" alignItems="center" padding="20px">
                    <Box>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{mode === "edit" ? `Edit ${practiceSession.title}` : "Create new practice session"}</Typography>
                        {mode === "create" && <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Create new practice session by providing all required details</Typography>}
                        {mode === "edit" && <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Update the practice session title, reference materials and access groups.</Typography>}
                    </Box>
                    <IconButton onClick={() => onClose( false )} >{Icons.default.CloseIcon}</IconButton>
                </Box>
                <Box flexGrow={1} overflow="auto" bgcolor={palette.form.formCardBg} height="100%" padding={layout.pagePadding} >
                    <Formik onSubmit={mode === "edit" ? updatePracticeSession : createPracticeSessions} validationSchema={validationSchema} initialValues={initialValues} >
                        {( { values, setFieldValue, errors } ) => {

                            return (
                                <Form>
                                    <FormControl id="createPracticeSessionFormControlForTitle" fullWidth>
                                        <Typography component="label" htmlFor='title' gutterBottom variant='subtitle2' color={palette.form.labelColor}>Name of the practice session *</Typography>
                                        <Field as={TextField} size="small" onFocus={event => event.target.select()} id="title" placeholder="Title; Provide a consise title to topics covered." type="text" name="title" />
                                        <FormHelperText><ErrorMessage name='title' /></FormHelperText>
                                    </FormControl>

                                    <FormControl id="createPracticeSessionFormControlForAcYear" fullWidth margin='normal'>
                                        <Box justifyContent="space-between" display="flex" gap="10px" alignItems="flex-end">
                                            <Typography component="label" htmlFor='academicYearInput' gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                                                Select academic year *
                                            </Typography>
                                            <Button id="academicYearRefreshBtn" disabled={refreshing === "years"} startIcon={refreshing === "years" ? <CircularProgress size={14} /> : Icons.default.Sync} onClick={refreshYears} sx={{ width: "fit-content", flexShrink: 0, fontSize: "12px" }} color="secondary" >
                                                {refreshing === "years" ? "Refreshing academic years..." : "Refresh academic years"}
                                            </Button>
                                        </Box>
                                        <CustomSelectField itemValueKey={"ac_year_id"} renderOptionText={item => item.ac_year} data={UserStore.getUserAcademicYears} placeholder='Academic Year' size="small" onChange={event => handleAcademicYearChange( event, setFieldValue )} value={values.academicYear} />
                                    </FormControl>

                                    {/* {values.academicYear && <FormControl id="createPracticeSessionFormControlForSubject" margin='normal' fullWidth>
                                        <Typography component="label" gutterBottom variant='subtitle2' color={palette.form.labelColor}>Choose the subject based on academic year {UserStore.getUserAcademicYears.find( ac => ac.ac_year_id === values.academicYear )?.ac_year} *</Typography>
                                        <Field
                                            id="subjectSelector"
                                            size="small"
                                            as={Select}
                                            displayEmpty
                                            disabled={!values.academicYear}
                                            sx={{ color: !values.subject && palette.form.placeholder, textTransform: "capitalize" }}
                                            value={fetchingSubjects ? "" : values?.subject?.subject_id || ""}
                                            onChange={e => handleSubjectChange( e, setFieldValue, values.start_date )}
                                            name="subject"
                                        >
                                            <MenuItem value="" dense disabled sx={{ fontWeight: "400" }} >Select subject</MenuItem>
                                            {( UserStore.getUserSubjectsDetails?.currentSubjects || [] ).filter( sub => sub.ac_year_id === values.academicYear ).map( ( sub, index ) => {
                                                return ( <MenuItem id={`subjectOption${index}`} sx={{ textTransform: "capitalize" }} dense key={sub?.subject_id} value={sub?.subject_id}>
                                                    <Box display="flex" flexDirection="column">
                                                        <Typography variant='subtitle2'>
                                                            {`${sub.subject_name?.toLowerCase()} (${sub.subject_code})`}
                                                        </Typography>
                                                        <Typography fontSize="12px" component="span">
                                                            {`${sub.ac_year}, ${sub.year ? `Year: ${sub.year}` : ""} ${sub.sem ? `Semester: ${sub.sem}` : ""}`}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem> )
                                            } )
                                            }
                                        </Field>
                                        {UserStore.getErrorsStatuses?.subjects && <Box bgcolor={palette.errorMessage.light + "22"} marginTop="10px" borderRadius="5px" padding="5px 10px" border={`1px dotted ${palette.errorMessage.main}`} display="flex" alignItems="center" gap="5px" fontSize="12px" color="textSecondary">
                                            <Typography color="errorMessage.main" variant='subtitle2'>Failed to fetch <strong><em>subjects</em></strong> from ERP...</Typography>
                                            <Button color="errorMessage" sx={{ textTransform: "capitalize" }} onClick={() => getFacultySubjects()} endIcon={fetchingSubjects ? <CircularProgress size={14} /> : Icons.default.Replay}>Retry</Button>
                                        </Box>}
                                        {fetchingSubjects && !UserStore.getErrorsStatuses?.subjects && <DataLoadingSpinner waitingMessage={`Fetching your subjects from ERP...`} padding='0' size={14} />}
                                        <FormHelperText> <ErrorMessage name="subject" /> </FormHelperText>
                                    </FormControl>} */}

                                    {values.subject && <FormControl id="createPracticeSessionFormControlForStudentGroups" margin='normal' fullWidth>
                                        <Box justifyContent="space-between" display="flex" gap="10px" alignItems="flex-end">
                                            <Typography display="block" variant="subtitle2" color={palette.form.labelColor} gutterBottom >
                                                Select single or multiple batchs/sections for students to practice.
                                            </Typography>
                                            <Button disabled={refreshing === "batches" || !values.academicYear} startIcon={refreshing === "batches" ? <CircularProgress size={14} /> : !values.academicYear ? Icons.default.ArrowBack : Icons.default.Sync} onClick={() => refreshBatches( values.academicYear )} sx={{ width: "fit-content", flexShrink: 0, marginTop: "2px", fontSize: "12px" }} color="secondary" >
                                                {refreshing === "batches" ? "Refreshing batches..." : !values.academicYear ? "Please select a academic year." : "Refresh batches"}
                                            </Button>
                                        </Box>

                                        <CustomSelectField
                                            onChange={event => handleAccessGroupChange( event, setFieldValue )}
                                            options={{ showCheckbox: true }}
                                            slotProps={{
                                                input: {
                                                    sx: {
                                                        padding: values.access_groups?.length > 0 ? "5px" : ""
                                                    }
                                                }
                                            }}
                                            searchFilter={( item, searchTerm ) => {
                                                return item.short_title?.toLowerCase()?.includes( searchTerm?.toLowerCase() )
                                            }}
                                            renderValue={value => {
                                                if ( value?.length === 0 ) {
                                                    return "Select batches and sections."
                                                } else {
                                                    return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: "5px" }}>
                                                        {values.access_groups.map( ( ag, index ) => (
                                                            <Box padding="2px 5px" bgcolor={palette.contentBg} border={border[1]} borderRadius="10px" key={ag.id} display="flex" alignItems="center">
                                                                <Typography fontSize="12px" variant='subtitle2'>
                                                                    {ag.chip_label}
                                                                </Typography>
                                                                <IconButton onMouseDown={e => e.stopPropagation()} onClick={event => removeSelectedAccessGroup( event, index, values, setFieldValue )} size='small' color='error'>
                                                                    {Icons.default.CancelIcon}
                                                                </IconButton>
                                                                {/* <Typography color='textSecondary' fontSize="12px" component="span">
                                                                            {ag.academic_details}
                                                                        </Typography> */}
                                                            </Box>
                                                        ) )}
                                                    </Box>
                                                }
                                            }}
                                            size="small"
                                            value={values.selected_access_groups || ""}
                                            data={{
                                                sections: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "section" ) ),
                                                batches: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "batch" ) )
                                            }}
                                            id="batchesAndSectionsInput"
                                            itemValueKey="id"
                                            multiple
                                            renderOptionText={item => <Box display="flex" flexDirection="column">
                                                <Typography variant='subtitle2'>
                                                    {item.short_title}
                                                </Typography>
                                                <Box display="flex" gap="5px" alignItems="center">
                                                    <CustomChip tooltipTitle='Course short name' value={item.course_name_short} />
                                                    <CustomChip tooltipTitle='Year' value={`Year: ${item.current_year}`} />
                                                    {item.current_year && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.current_sem}`} />}
                                                </Box>
                                            </Box>}
                                        />
                                        <FormHelperText><ErrorMessage name='access_groups' /></FormHelperText>
                                    </FormControl>}

                                    {mode !== "edit" && <Box
                                        marginTop="20px"
                                        id="createPracticeSessionFormControlForDateRange"
                                    >
                                        <Typography variant="subtitle2" color={palette.form.labelColor} gutterBottom >Select date range *</Typography>
                                        <Box display="flex" flexWrap="wrap" gap="10px" alignItems="center">
                                            <FormControl sx={{ minWidth: "170px", width: "250px" }}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                        sx={{ width: "100%", fontSize: "12px", '& input': { padding: "10px" } }}
                                                        disableToolbar
                                                        id="start-date"
                                                        variant="inline"
                                                        format="DD-MM-YYYY"
                                                        value={dayjs( values.date_range_start )}
                                                        onChange={val => {
                                                            if ( val && val.isValid() )
                                                                setFieldValue( 'date_range_start', val )
                                                        }}
                                                        KeyboardButtonProps={{
                                                            'aria-label': 'change date',
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </FormControl>
                                            <Typography variant='subtitle2' color='textSecondary'>to</Typography>
                                            <FormControl sx={{ minWidth: "170px", width: "250px" }}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                        sx={{ width: "100%", fontSize: "12px", '& input': { padding: "10px" } }}
                                                        disableToolbar
                                                        id="start-date"
                                                        variant="inline"
                                                        format="DD-MM-YYYY"
                                                        maxDate={dayjs()}
                                                        value={dayjs( values.date_range_end )}
                                                        onChange={val => {
                                                            if ( val && val.isValid() )
                                                                setFieldValue( 'date_range_end', val )
                                                        }}
                                                        KeyboardButtonProps={{
                                                            'aria-label': 'change date',
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </FormControl>
                                        </Box>
                                        <FormHelperText>
                                            <ErrorMessage name="date_range_start" />
                                        </FormHelperText>
                                    </Box>
                                    }

                                    {values.subject && <FormControl id="createPracticeSessionFormControlForTitle" margin="normal" fullWidth>
                                        <Typography
                                            gutterBottom
                                            variant="subtitle2"
                                            color={palette.form.labelColor}
                                        >
                                            Add modules and topics for the practice *
                                        </Typography>
                                        {Object.keys( values.modules_and_topics ).length === 0 && <AddModuleButton onselect={( mod ) => handleNewModuleSelect( mod, values, setFieldValue )} modules={MODULE_OPTIONS.filter( mod => !Object.keys( values.modules_and_topics ).includes( mod ) )} />}

                                        {participants?.length > 0 && <Box marginBottom="10px" padding="10px" borderRadius="10px" alignItems="center" bgcolor={palette.warning.light + "22"} border={`1px dashed ${palette.warning.main}`} display="flex" gap="10px" color={palette.warning.main}>
                                            {Icons.default.Info}
                                            <Typography variant='caption' color='warning'>To ensure a uniform experience, topic updates are disabled after student participation begins.</Typography>
                                        </Box>}

                                        {/* MODULE DETAILS */}
                                        {Object.keys( values.modules_and_topics ).length > 0 && <Box display="flex" flexDirection="column" gap="10px">
                                            {Object.keys( values.modules_and_topics ).map( module => {

                                                const moduleDetails = values.modules_and_topics[module]

                                                return (
                                                    <Box padding="10px" key={module} border={border[1]} overflow="clip" borderRadius={layout.borderRadius} display="flex" flexDirection="column" gap={layout.cardsGap}>

                                                        {/* MODULE HEADER */}
                                                        <Box display="flex" gap={layout.cardsGap}   >
                                                            <Typography width="fit-content" bgcolor={palette.customThemeColor.main} display="flex" alignItems="center" fontSize="12px" padding="2px 5px" borderRadius={layout.borderRadius} color={palette.customThemeColor.contrastText} variant="subtitle2">
                                                                {module.toUpperCase()}
                                                            </Typography>
                                                            <Button
                                                                variant='outlined'
                                                                size="small"
                                                                color="error"
                                                                onClick={() =>
                                                                    removeModule( module, values, setFieldValue )
                                                                }
                                                                sx={{ flexShrink: 0 }}
                                                                startIcon={Icons.default.CloseIcon}
                                                                disabled={participants?.length > 0}
                                                            >
                                                                Remove {module}
                                                            </Button>

                                                        </Box>

                                                        <Box id="createPracticeSessionFormControlForModuleName">
                                                            <Typography component="label" htmlFor='title' display="block" gutterBottom variant='subtitle2' color={palette.form.labelColor}>Module Name *</Typography>
                                                            <TextField
                                                                id="moduleNameInput"
                                                                fullWidth
                                                                onFocus={event => event.target.select()}
                                                                size='small'
                                                                value={moduleDetails.name}
                                                                onChange={( e ) => updateModuleName( module, e.target.value, values, setFieldValue )}
                                                                placeholder={`Enter ${module} name...`}
                                                            />
                                                        </Box>

                                                        {/* ADD TOPIC */}
                                                        <Box id="createPracticeSessionFormControlForModuleTopics">
                                                            <Typography component="label" htmlFor='title' display="block" gutterBottom variant='subtitle2' color={palette.form.labelColor}>Topics covered from {formatDate( values.date_range_start )} to {formatDate( values.date_range_end )} *</Typography>
                                                            <Box display="flex" flexWrap="wrap" gap="5px">
                                                                <TextField
                                                                    id="topicsInput"
                                                                    fullWidth
                                                                    placeholder='Type topics and press enter or click on add button to add.'
                                                                    size="small"
                                                                    value={values[`${module}-topicInput`] || ""}
                                                                    onChange={event => setFieldValue( `${module}-topicInput`, event.target.value )}
                                                                    onKeyDown={event => handleNewTopicAdd( event, module, values, setFieldValue )}
                                                                    disabled={participants?.length > 0}
                                                                />
                                                                <Button
                                                                    sx={{ width: "max-content", flexShrink: 0 }}
                                                                    startIcon={Icons.default.AddIcon}
                                                                    variant="contained"
                                                                    disableElevation
                                                                    onClick={( event ) => handleNewTopicAdd( event, module, values, setFieldValue )}
                                                                    disabled={participants?.length > 0}
                                                                >
                                                                    Add Topic
                                                                </Button>
                                                            </Box>
                                                            {/* TOPIC TAGS */}
                                                            {moduleDetails.topics.length > 0 && <Box marginTop="10px" display="flex" flexWrap="wrap" gap="5px">
                                                                {moduleDetails.topics.map(
                                                                    ( topic, index ) => (
                                                                        <Box
                                                                            key={topic}
                                                                            border={`1px solid ${palette.common.font}`}
                                                                            bgcolor={palette.contentBg}
                                                                            borderRadius={layout.borderRadius}
                                                                            px="6px"
                                                                            py="2px"
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gap="5px"
                                                                        >
                                                                            <Typography fontSize="10px" width="20px" sx={{ aspectRatio: "1 / 1" }} display="flex" alignItems="center" justifyContent="center" borderRadius="50%" border={`1px solid ${palette.common.font}`} >{index + 1}</Typography>
                                                                            <Typography marginRight="10px" fontSize="12px">{topic}</Typography>
                                                                            <IconButton
                                                                                color='error'
                                                                                disabled={participants?.length > 0}
                                                                                size="small"
                                                                                onClick={() =>
                                                                                    removeTopic(
                                                                                        module,
                                                                                        index,
                                                                                        values,
                                                                                        setFieldValue
                                                                                    )
                                                                                }
                                                                            >
                                                                                {Icons.default.CloseIcon}
                                                                            </IconButton>
                                                                        </Box>
                                                                    )
                                                                )}
                                                            </Box>}
                                                        </Box>
                                                    </Box>
                                                )
                                            } )}
                                            {/* <AddModuleButton label="Add another module" onselect={( mod ) => handleNewModuleSelect( mod, values, setFieldValue )} modules={MODULE_OPTIONS.filter( mod => !Object.keys( values.modules_and_topics ).includes( mod ) )} /> */}
                                        </Box>}

                                        <FormHelperText>
                                            <ErrorMessage name="modules_and_topics" />
                                        </FormHelperText>
                                    </FormControl>}

                                    {values.subject && <FormControl id="createPracticeSessionFormControlForComplexity" fullWidth>
                                        <Typography component="label" htmlFor='title' gutterBottom variant='subtitle2' color={palette.form.labelColor}>Choose quiz difficulty level *</Typography>
                                        <Field
                                            as={CustomSelectField}
                                            size="small"
                                            id="createPracticeSessionFormControlForComplexity"
                                            placeholder="Select difficulty level"
                                            data={complexityOptions}
                                            onChange={event => setFieldValue( 'complexity', event.target.value )}
                                            value={values.complexity}
                                            renderOptionText={item => item.label}
                                            itemValueKey="value"
                                        />
                                    </FormControl>}

                                    <FormControl margin='normal'>
                                        <Typography variant="subtitle2" color={palette.form.labelColor} gutterBottom >Specify a deadline to restrict practice after a certain date. (optional)</Typography>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                sx={{ width: "100%", fontSize: "12px", '& input': { padding: "10px" } }}
                                                disableToolbar
                                                id="close-date"
                                                variant="inline"
                                                format="DD-MM-YYYY"
                                                minDate={dayjs( practiceSession.created_at ).add( 5, 'days' ) > dayjs() ? dayjs( practiceSession.created_at ).add( 5, 'days' ) : dayjs()}
                                                value={dayjs( values.close_date ).subtract( 1, 'day' )}
                                                onChange={val => {
                                                    if ( val && val.isValid() )
                                                        setFieldValue( 'close_date', dayjs( val ).add( 1, 'day' ) )
                                                }}
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date',
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>


                                    <Box justifyContent="flex-end" display="flex" marginTop="40px" gap="10px">
                                        <Button variant='outlined' disabled={creating} onClick={() => onClose( false )} color="error" startIcon={Icons.default.CloseIcon} sx={{ width: "max-content", textTransform: "capitalize" }} >Cancel</Button>
                                        <Button id="practiceSessionCreateSubmitBtn" disabled={creating} type='submit' variant='contained' disableElevation color="primary" startIcon={creating ? <CircularProgress size={20} /> : mode === 'edit' ? Icons.default.CheckIcon : Icons.default.AddIcon} sx={{ width: "max-content", textTransform: "capitalize" }} >{mode === "edit" ? "Save changes" : "Create Practice session"}</Button>
                                    </Box>
                                </Form>
                            )
                        }}
                    </Formik>
                </Box>
            </Box>
        </Dialog >
    )
} )

export default EditPracticeSession
